import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { HelpCTA } from "@/components/HelpCTA";
import { GALLERY } from "@/data/content";
import { Seo } from "@/components/Seo";

const spanClass = {
  tall: "md:row-span-2",
  wide: "md:col-span-2",
  "": "",
};

const Gallery = () => {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="gallery-page" className="pt-20">
      <Seo
        title="Gallery — Sober Life Foundation, Ulhasnagar"
        description="A glimpse inside our rehabilitation centre — safe, clean and caring spaces where recovery begins for men and women in Ulhasnagar."
        path="/gallery"
      />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-14">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-teal mb-6">Gallery</p>
          <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl text-maroon-deep leading-[0.98] max-w-4xl">
            Moments of hope, healing and community
          </h1>
          <p className="mt-8 max-w-2xl text-stone-600 text-lg leading-relaxed">
            A glimpse into the safe, warm and nurturing spaces where recovery begins. Every image reflects our
            promise of dignity, care and a brighter tomorrow.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="grid grid-cols-2 md:grid-cols-3 auto-rows-[220px] md:auto-rows-[260px] gap-4">
          {GALLERY.map((img, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className={`${spanClass[img.span]} h-full`}>
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setActive(img)}
                className="group relative w-full h-full overflow-hidden rounded-2xl block"
              >
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-maroon-deep/0 group-hover:bg-maroon-deep/30 transition-colors duration-500" />
                <span className="absolute bottom-4 left-4 right-4 text-left text-sm text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 drop-shadow">
                  {img.alt}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[60] bg-maroon-deep/90 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <button data-testid="lightbox-close" className="absolute top-6 right-6 text-cream hover:text-gold transition-colors" onClick={() => setActive(null)}>
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.33, 1, 0.68, 1] }}
              src={active.src}
              alt={active.alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>

      <HelpCTA />
    </div>
  );
};

export default Gallery;
